import { useState } from 'react';
import Constants from '../../utilities/constants/Constants'; // eslint-disable-line no-unused-vars
import { updateStudentSchoolAdmin, getStudentsByUserEmail } from '@dataconnect/generated';

const AdminUserManagement = ({ onBack }) => {
  const [addEmail, setAddEmail] = useState('');
  const [removeEmail, setRemoveEmail] = useState('');
  const [checkEmail, setCheckEmail] = useState('');
  const [schoolAdminEmail, setSchoolAdminEmail] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [checkLoading, setCheckLoading] = useState(false);
  const [schoolAdminLoading, setSchoolAdminLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [checkResult, setCheckResult] = useState(null);

  // Firebase Functions HTTP endpoints from environment variables
  const ADD_ADMIN_URL = process.env.REACT_APP_ADD_ADMIN_URL;
  const REMOVE_ADMIN_URL = process.env.REACT_APP_REMOVE_ADMIN_URL;
  const CHECK_ADMIN_URL = process.env.REACT_APP_CHECK_ADMIN_URL;

  const showMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!addEmail) {
      showMessage('Please enter an email address', 'danger');
      return;
    }

    setAddLoading(true);
    try {
      const response = await fetch(`${ADD_ADMIN_URL}?email=${encodeURIComponent(addEmail)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (response.ok && result.success) {
        showMessage(result.message, 'success');
        setAddEmail('');
      } else {
        showMessage(result.error || 'Failed to add admin role', 'danger');
      }
    } catch (error) {

      showMessage('Network error: Failed to add admin role', 'danger');
    } finally {
      setAddLoading(false);
    }
  };

  const handleRemoveAdmin = async (e) => {
    e.preventDefault();
    if (!removeEmail) {
      showMessage('Please enter an email address', 'danger');
      return;
    }

    if (!window.confirm(`Are you sure you want to remove admin role from ${removeEmail}?`)) {
      return;
    }

    setRemoveLoading(true);
    try {
      const response = await fetch(`${REMOVE_ADMIN_URL}?email=${encodeURIComponent(removeEmail)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (response.ok && result.success) {
        showMessage(result.message, 'success');
        setRemoveEmail('');
      } else {
        showMessage(result.error || 'Failed to remove admin role', 'danger');
      }
    } catch (error) {

      showMessage('Network error: Failed to remove admin role', 'danger');
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleCheckAdmin = async (e) => {
    e.preventDefault();
    if (!checkEmail) {
      showMessage('Please enter an email address', 'danger');
      return;
    }

    setCheckLoading(true);
    setCheckResult(null);
    
    try {
      // Call the server-side HTTP endpoint to check admin status
      const response = await fetch(`${CHECK_ADMIN_URL}?email=${encodeURIComponent(checkEmail)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Server successfully checked the user
        setCheckResult({
          email: result.email,
          uid: result.uid,
          isAdmin: result.isAdmin,
          hasCustomClaims: result.hasCustomClaims,
          customClaims: result.customClaims,
          method: 'server-side-check',
          timestamp: new Date(result.checkedAt).toLocaleString()
        });
        
        const statusMessage = result.isAdmin 
          ? `✅ ${result.email} has admin privileges`
          : `❌ ${result.email} does not have admin privileges`;
        
        showMessage(statusMessage, result.isAdmin ? 'success' : 'warning');
        
      } else {
        // Server returned an error
        if (response.status === 404) {
          showMessage(`User with email ${checkEmail} not found. User must have a Firebase account.`, 'warning');
          setCheckResult({
            email: checkEmail,
            isAdmin: false,
            method: 'server-side-check',
            error: 'User not found',
            timestamp: new Date().toLocaleString()
          });
        } else {
          showMessage(result.error || 'Failed to check admin status', 'danger');
        }
      }
      
    } catch (error) {

      showMessage('Network error: Failed to check admin status', 'danger');
    } finally {
      setCheckLoading(false);
    }
  };

  const handleMakeSchoolAdmin = async (e) => {
    e.preventDefault();
    if (!schoolAdminEmail) {
      showMessage('Please enter a student email address', 'danger');
      return;
    }

    setSchoolAdminLoading(true);
    try {
      // First, get the student(s) by email
      const studentsResult = await getStudentsByUserEmail({ email: schoolAdminEmail });
      
      if (!studentsResult.data?.students || studentsResult.data.students.length === 0) {
        showMessage('No student found with that email address', 'danger');
        return;
      }

      const students = studentsResult.data.students;
      
      // If multiple students, show them and let user confirm
      if (students.length > 1) {
        const studentList = students.map((s, i) => `${i + 1}. ${s.name} (Grade ${s.gradeLevel})`).join('\n');
        if (!window.confirm(`Found ${students.length} students:\n${studentList}\n\nMake ALL of them school admins?`)) {
          return;
        }
      } else {
        if (!window.confirm(`Make ${students[0].name} (Grade ${students[0].gradeLevel}) a school admin?`)) {
          return;
        }
      }

      // Update each student
      let successCount = 0;
      for (const student of students) {
        const result = await updateStudentSchoolAdmin({
          id: student.id,
          isSchoolAdmin: true
        });

        if (result.data?.student_update !== null) {
          successCount++;
        }
      }

      if (successCount === students.length) {
        showMessage(`✅ Successfully made ${successCount} student(s) school admin(s)`, 'success');
        setSchoolAdminEmail('');
      } else {
        showMessage(`Updated ${successCount} of ${students.length} students. Some updates may have failed.`, 'warning');
      }
    } catch (error) {
      console.error('Failed to make school admin:', error);
      showMessage(`Failed to make student a school admin: ${error.message}`, 'danger');
    } finally {
      setSchoolAdminLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-200">
      <div className="w-full py-6 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-1">User Management</h1>
              <p className="text-gray-600">Manage admin roles and permissions</p>
            </div>
            <button 
              className="border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium py-2 px-4 rounded-xl flex items-center gap-2 transition-colors" 
              onClick={onBack}
            >
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
              </svg>
              Back
            </button>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-lg">
            {message && (
              <div className={`mx-6 pt-6 px-4 py-3 rounded-xl ${
                messageType === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
                messageType === 'danger' ? 'bg-red-50 text-red-700 border border-red-200' :
                messageType === 'warning' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
                'bg-blue-50 text-blue-700 border border-blue-200'
              }`} role="alert">
                <div className="flex justify-between items-start">
                  <span>{message}</span>
                  <button 
                    type="button" 
                    className="ml-4 text-gray-400 hover:text-gray-600"
                    onClick={() => setMessage('')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6 p-6">
                  <div>
                    <div className="h-full border-0 shadow-sm rounded-xl transition-all duration-200 hover:-translate-y-1 hover:shadow-lg bg-white">
                      <div className="border-0 bg-transparent pt-6 pb-4 px-6">
                        <div className="flex items-center">
                          <div className="w-12 h-12 bg-emerald-500 text-white rounded-full flex items-center justify-center mr-4">
                            <svg width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                              <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
                            </svg>
                          </div>
                          <div>
                            <h5 className="text-lg font-semibold text-gray-900 mb-1">Add Admin</h5>
                            <p className="text-gray-500 text-sm">Grant admin privileges</p>
                          </div>
                        </div>
                      </div>
                      <div className="px-6 pb-6">
                        <form onSubmit={handleAddAdmin}>
                          <div className="mb-4">
                            <label htmlFor="addEmail" className="block text-sm font-medium text-gray-700 mb-2">
                              Email Address
                            </label>
                            <input
                              type="email"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors"
                              id="addEmail"
                              value={addEmail}
                              onChange={(e) => setAddEmail(e.target.value)}
                              placeholder="user@example.com"
                              required
                            />
                          </div>
                          <button
                            type="submit"
                            className="w-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-400 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            disabled={addLoading}
                          >
                            {addLoading ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Adding...
                              </>
                            ) : (
                              <>
                                <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                  <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
                                </svg>
                                Add Admin Role
                              </>
                            )}
                          </button>
                        </form>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="h-full border-0 shadow-sm rounded-xl transition-all duration-200 hover:-translate-y-1 hover:shadow-lg bg-white">
                      <div className="border-0 bg-transparent pt-6 pb-4 px-6">
                        <div className="flex items-center">
                          <div className="w-12 h-12 bg-red-500 text-white rounded-full flex items-center justify-center mr-4">
                            <svg width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                              <path d="M4 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 4 8z"/>
                            </svg>
                          </div>
                          <div>
                            <h5 className="text-lg font-semibold text-gray-900 mb-1">Remove Admin</h5>
                            <p className="text-gray-500 text-sm">Revoke admin privileges</p>
                          </div>
                        </div>
                      </div>
                      <div className="px-6 pb-6">
                        <form onSubmit={handleRemoveAdmin}>
                          <div className="mb-4">
                            <label htmlFor="removeEmail" className="block text-sm font-medium text-gray-700 mb-2">
                              Email Address
                            </label>
                            <input
                              type="email"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors"
                              id="removeEmail"
                              value={removeEmail}
                              onChange={(e) => setRemoveEmail(e.target.value)}
                              placeholder="admin@example.com"
                              required
                            />
                          </div>
                          <button
                            type="submit"
                            className="w-full bg-red-500 hover:bg-red-600 disabled:bg-red-400 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            disabled={removeLoading}
                          >
                            {removeLoading ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Removing...
                              </>
                            ) : (
                              <>
                                <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                  <path d="M4 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 4 8z"/>
                                </svg>
                                Remove Admin Role
                              </>
                            )}
                          </button>
                        </form>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="h-full border-0 shadow-sm rounded-xl transition-all duration-200 hover:-translate-y-1 hover:shadow-lg bg-white">
                      <div className="border-0 bg-transparent pt-6 pb-4 px-6">
                        <div className="flex items-center">
                          <div className="w-12 h-12 bg-blue-500 text-white rounded-full flex items-center justify-center mr-4">
                            <svg width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
                            </svg>
                          </div>
                          <div>
                            <h5 className="text-lg font-semibold text-gray-900 mb-1">Check Status</h5>
                            <p className="text-gray-500 text-sm">Verify admin privileges</p>
                          </div>
                        </div>
                      </div>
                      <div className="px-6 pb-6">
                        <form onSubmit={handleCheckAdmin}>
                          <div className="mb-4">
                            <label htmlFor="checkEmail" className="block text-sm font-medium text-gray-700 mb-2">
                              Email Address
                            </label>
                            <input
                              type="email"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                              id="checkEmail"
                              value={checkEmail}
                              onChange={(e) => setCheckEmail(e.target.value)}
                              placeholder="user@example.com"
                              required
                            />
                          </div>
                          <button
                            type="submit"
                            className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-blue-400 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-4"
                            disabled={checkLoading}
                          >
                            {checkLoading ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Checking...
                              </>
                            ) : (
                              <>
                                <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                  <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
                                </svg>
                                Check Status
                              </>
                            )}
                          </button>
                        </form>
                        
                        {checkResult && (
                          <div className="border-top pt-3">
                            <div className="p-3 rounded-3" style={{
                              backgroundColor: checkResult.error ? '#fef2f2' : (checkResult.isAdmin ? '#f0fdf4' : '#fffbeb'),
                              border: `1px solid ${checkResult.error ? '#fecaca' : (checkResult.isAdmin ? '#bbf7d0' : '#fed7aa')}`
                            }}>
                              <div className="d-flex align-items-start">
                                <div className="me-3">
                                  <div className="rounded-circle d-flex align-items-center justify-content-center" style={{
                                    width: '40px',
                                    height: '40px',
                                    backgroundColor: checkResult.error ? '#ef4444' : (checkResult.isAdmin ? '#10b981' : '#f59e0b'),
                                    color: 'white',
                                    fontSize: '18px'
                                  }}>
                                    {checkResult.error ? '⚠' : (checkResult.isAdmin ? '✓' : '✗')}
                                  </div>
                                </div>
                                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                  <div className="mb-2">
                                    <h6 className="mb-1 fw-semibold">{checkResult.email}</h6>
                                    <span className={`badge ${checkResult.error ? 'bg-danger' : (checkResult.isAdmin ? 'bg-success' : 'bg-warning')}`}>
                                      {checkResult.error ? checkResult.error : (checkResult.isAdmin ? 'Admin User' : 'Regular User')}
                                    </span>
                                  </div>
                                  
                                  {checkResult.uid && (
                                    <div className="mb-2">
                                      <div className="small text-muted">
                                        <strong>User ID:</strong>
                                      </div>
                                      <code className="small" style={{ 
                                        backgroundColor: 'rgba(0,0,0,0.05)', 
                                        padding: '2px 6px', 
                                        borderRadius: '4px',
                                        wordBreak: 'break-all',
                                        fontSize: '11px'
                                      }}>
                                        {checkResult.uid}
                                      </code>
                                    </div>
                                  )}
                                  
                                  {checkResult.hasCustomClaims !== undefined && (
                                    <div className="mb-2">
                                      <div className="small">
                                        <strong className="text-muted">Custom Claims:</strong> 
                                        <span className="ms-1">
                                          {checkResult.hasCustomClaims ? 'Present' : 'None'}
                                        </span>
                                      </div>
                                      {checkResult.customClaims && Object.keys(checkResult.customClaims).length > 0 && (
                                        <div className="mt-1">
                                          <pre className="small mb-0" style={{ 
                                            backgroundColor: 'rgba(0,0,0,0.05)',
                                            padding: '8px',
                                            borderRadius: '6px',
                                            fontSize: '11px', 
                                            maxHeight: '80px', 
                                            overflow: 'auto',
                                            wordWrap: 'break-word',
                                            whiteSpace: 'pre-wrap',
                                            border: '1px solid rgba(0,0,0,0.1)'
                                          }}>
                                            {JSON.stringify(checkResult.customClaims, null, 2)}
                                          </pre>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  <div className="mt-3 pt-2" style={{ borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                                    <div className="row">
                                      <div className="col-6">
                                        <div className="small text-muted">
                                          <strong>Method:</strong><br/>
                                          <span>{checkResult.method}</span>
                                        </div>
                                      </div>
                                      <div className="col-6">
                                        <div className="small text-muted">
                                          <strong>Checked:</strong><br/>
                                          <span>{checkResult.timestamp}</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="h-full border-0 shadow-sm rounded-xl transition-all duration-200 hover:-translate-y-1 hover:shadow-lg bg-white">
                      <div className="border-0 bg-transparent pt-6 pb-4 px-6">
                        <div className="flex items-center">
                          <div className="w-12 h-12 bg-purple-500 text-white rounded-full flex items-center justify-center mr-4">
                            <svg width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                              <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
                            </svg>
                          </div>
                          <div>
                            <h5 className="text-lg font-semibold text-gray-900 mb-1">School Admin</h5>
                            <p className="text-gray-500 text-sm">Grant school admin role</p>
                          </div>
                        </div>
                      </div>
                      <div className="px-6 pb-6">
                        <form onSubmit={handleMakeSchoolAdmin}>
                          <div className="mb-4">
                            <label htmlFor="schoolAdminEmail" className="block text-sm font-medium text-gray-700 mb-2">
                              Student Email Address
                            </label>
                            <input
                              type="email"
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                              id="schoolAdminEmail"
                              value={schoolAdminEmail}
                              onChange={(e) => setSchoolAdminEmail(e.target.value)}
                              placeholder="user@example.com"
                              required
                            />
                            <p className="text-xs text-gray-500 mt-1">Enter the parent/user email associated with the student</p>
                          </div>
                          <button
                            type="submit"
                            className="w-full bg-purple-500 hover:bg-purple-600 disabled:bg-purple-400 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            disabled={schoolAdminLoading}
                          >
                            {schoolAdminLoading ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Processing...
                              </>
                            ) : (
                              <>
                                <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                  <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/>
                                </svg>
                                Make School Admin
                              </>
                            )}
                          </button>
                        </form>
                      </div>
                    </div>
                  </div>
            </div>

            {/* Security Guidelines */}
            <div className="mt-6 bg-slate-50 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-indigo-500 text-white rounded-full flex items-center justify-center mr-3">
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zM3 6V3a5 5 0 0 1 10 0v3a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1z"/>
                  </svg>
                </div>
                <h6 className="text-lg font-semibold text-gray-900">Security & Guidelines</h6>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      Admin roles managed via secure HTTP endpoints
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      All operations server-side validated
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      Users need existing Firebase accounts
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      School admins can create school-level syllabus
                    </li>
                  </ul>
                </div>
                <div>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      Changes take effect immediately
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      Separate forms prevent accidents
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      Real-time privilege verification
                    </li>
                    <li className="flex items-start">
                      <span className="text-green-500 mr-2">•</span>
                      Student must have school association
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUserManagement;
